/**
 * Push Notification Service
 * Handles native push notifications for iOS PWA and all platforms
 */

class NotificationService {
  constructor() {
    this.swRegistration = null;
    this.isSupported = 'Notification' in window && 'serviceWorker' in navigator;
    // iOS detection — modern iPadOS reports `MacIntel` as platform but
    // exposes touch points and the iOS WebKit standard. Catch both the
    // legacy and the modern signatures so the in-app notification
    // prompt is suppressed on every Apple touch device (we defer to
    // the OS-level permission dialog instead).
    const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
    const isAppleTouch = /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1);
    this.isIOS = isAppleTouch && !window.MSStream;
    this.isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                        window.navigator.standalone === true;
    this.permissionGranted = false;
  }

  // Initialize the notification service
  async init() {
    if (!this.isSupported) {
      console.log('[Notifications] Not supported in this browser');
      return false;
    }

    try {
      // Register service worker
      this.swRegistration = await navigator.serviceWorker.register('/sw.js');
      console.log('[Notifications] Service Worker registered');

      // Check current permission
      this.permissionGranted = Notification.permission === 'granted';
      
      return true;
    } catch (error) {
      console.error('[Notifications] Service Worker registration failed:', error);
      return false;
    }
  }

  // Request notification permission
  async requestPermission() {
    if (!this.isSupported) {
      return 'unsupported';
    }

    if (Notification.permission === 'granted') {
      this.permissionGranted = true;
      return 'granted';
    }

    if (Notification.permission === 'denied') {
      return 'denied';
    }

    try {
      const permission = await Notification.requestPermission();
      this.permissionGranted = permission === 'granted';
      return permission;
    } catch (error) {
      console.error('[Notifications] Permission request failed:', error);
      return 'error';
    }
  }

  // Show a notification
  async show(title, options = {}) {
    if (!this.permissionGranted) {
      console.log('[Notifications] Permission not granted');
      return false;
    }

    const defaultOptions = {
      icon: '/icons/icon-192x192.png',
      badge: '/icons/icon-72x72.png',
      vibrate: [200, 100, 200],
      requireInteraction: false,
      silent: false,
      tag: 'uplift-' + Date.now(),
      renotify: true,
      data: {}
    };

    const notificationOptions = { ...defaultOptions, ...options };

    try {
      // Use Service Worker for notifications when available (required for iOS PWA)
      if (this.swRegistration) {
        await this.swRegistration.showNotification(title, notificationOptions);
        console.log('[Notifications] Shown via Service Worker');
      } else {
        // Fallback to Notification API
        new Notification(title, notificationOptions);
        console.log('[Notifications] Shown via Notification API');
      }
      return true;
    } catch (error) {
      console.error('[Notifications] Failed to show notification:', error);
      return false;
    }
  }

  // Show incoming call notification
  async showIncomingCall(caller, callId, isVideo = true) {
    const title = `${caller.name || 'Unknown'} is calling`;
    const options = {
      body: `Incoming ${isVideo ? 'video' : 'voice'} call`,
      icon: caller.profile_picture || '/icons/icon-192x192.png',
      badge: '/icons/icon-72x72.png',
      tag: `call-${callId}`,
      requireInteraction: true,
      vibrate: [200, 100, 200, 100, 200, 100, 200],
      actions: [
        { action: 'answer', title: 'Answer', icon: '/icons/phone-answer.png' },
        { action: 'decline', title: 'Decline', icon: '/icons/phone-decline.png' }
      ],
      data: {
        type: 'video_call',
        callId: callId,
        callerId: caller.id
      }
    };

    return this.show(title, options);
  }

  // Show message notification
  async showMessage(sender, message, chatId) {
    const title = sender.name || 'New Message';
    const options = {
      body: message.length > 100 ? message.substring(0, 100) + '...' : message,
      icon: sender.profile_picture || '/icons/icon-192x192.png',
      badge: '/icons/icon-72x72.png',
      tag: `message-${chatId}`,
      requireInteraction: false,
      data: {
        type: 'message',
        chatId: chatId,
        senderId: sender.id
      }
    };

    return this.show(title, options);
  }

  // Show task notification
  async showTask(task) {
    const title = 'Task Update';
    const options = {
      body: task.title || 'You have a task update',
      icon: '/icons/icon-192x192.png',
      badge: '/icons/icon-72x72.png',
      tag: `task-${task.id}`,
      requireInteraction: false,
      data: {
        type: 'task',
        taskId: task.id
      }
    };

    return this.show(title, options);
  }

  // Show generic notification
  async showGeneric(title, body, url = null) {
    const options = {
      body: body,
      icon: '/icons/icon-192x192.png',
      badge: '/icons/icon-72x72.png',
      tag: 'generic-' + Date.now(),
      data: {
        type: 'generic',
        url: url
      }
    };

    return this.show(title, options);
  }

  // Check if running as installed PWA
  isPWA() {
    return this.isStandalone;
  }

  // Check if iOS
  isIOSDevice() {
    return this.isIOS;
  }

  // Get notification status
  getStatus() {
    return {
      supported: this.isSupported,
      permission: Notification.permission,
      serviceWorker: !!this.swRegistration,
      isIOS: this.isIOS,
      isPWA: this.isStandalone
    };
  }
}

// Singleton instance
const notificationService = new NotificationService();

export default notificationService;
